/* Soul Comfort CRO FAQ: progressive enhancement of native details/summary. */
(() => {
  if (customElements.get('cro-faq-accordion')) return;

  class CroFaqAccordion extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.animations = new Map();
      this.revision = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details'));
      // The native name attribute provides single-open behavior without JS.
      // Once enhanced, manage the group ourselves so closing can animate.
      this.items.forEach((details, index) => {
        details.removeAttribute('name');
        if (index > 0 && details.open) details.open = false;
      });
      this.addEventListener('click', this.handleClick);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
    }

    disconnectedCallback() {
      this.revision += 1;
      this.finishAnimations();
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    handleClick(event) {
      const summary = event.target.closest('summary');
      const details = summary && summary.parentElement;
      if (!details || !this.items.includes(details)) return;
      event.preventDefault();
      this.setActive(details, null);
    }

    handleBlockSelect(event) {
      const details = event.target.closest('details');
      if (details && this.items.includes(details)) this.setActive(details, true);
    }

    finishAnimations() {
      for (const animation of Array.from(this.animations.values())) {
        animation.finish();
      }
    }

    async setActive(details, requestedState) {
      const revision = ++this.revision;
      this.finishAnimations();
      const opening = requestedState === null ? !details.open : requestedState;

      if (!opening) {
        await this.animateDetails(details, false);
        return;
      }

      // Close the previous answer before opening another. No two expanded
      // answers are visible simultaneously, including during the transition.
      for (const other of this.items) {
        if (other !== details && other.open) {
          await this.animateDetails(other, false);
          if (revision !== this.revision) return;
        }
      }
      if (revision === this.revision && !details.open) {
        await this.animateDetails(details, true);
      }
    }

    animateDetails(details, opening) {
      if (details.open === opening) return Promise.resolve();

      const summary = details.querySelector(':scope > summary');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!summary || reducedMotion || typeof details.animate !== 'function') {
        details.open = opening;
        return Promise.resolve();
      }

      const startHeight = details.getBoundingClientRect().height;
      details.style.overflow = 'hidden';
      if (opening) details.open = true;
      const endHeight = opening ? details.scrollHeight : summary.getBoundingClientRect().height;

      if (Math.abs(startHeight - endHeight) < 1) {
        details.open = opening;
        details.style.removeProperty('overflow');
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const animation = details.animate(
          [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
          { duration: 240, easing: 'ease-in-out' }
        );
        this.animations.set(details, animation);
        const cleanup = () => {
          if (this.animations.get(details) !== animation) return;
          this.animations.delete(details);
          details.open = opening;
          details.style.removeProperty('overflow');
          resolve();
        };
        animation.onfinish = cleanup;
        animation.oncancel = cleanup;
      });
    }
  }

  customElements.define('cro-faq-accordion', CroFaqAccordion);
})();
